import { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { StaffHeader } from "@/components/StaffHeader";
import { Button } from "@/components/ui/button";
import { HeartPulse, Printer, Smartphone, ClipboardCheck, Store, FileDown } from "lucide-react";
import { downloadElementPdf } from "@/lib/pdf";

const PHARMACY = {
  name: "Pharmacie des Écoles",
  address: "8 Place André Allègre, 83250 La Londe-les-Maures",
  phone: "04 94 66 00 00",
};
const ACCENT = "#7C3AED";

export default function BilanPoster() {
  const url = useMemo(() => (typeof window !== "undefined" ? window.location.origin : "") + "/p/pharmacie-des-ecoles/bilan-prevention", []);
  const [pdfing, setPdfing] = useState(false);
  const savePdf = async () => {
    setPdfing(true);
    try { await downloadElementPdf(document.getElementById("print-area"), "affiche-bilan-sante.pdf"); }
    finally { setPdfing(false); }
  };

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="no-print"><StaffHeader /></div>

      <div className="no-print mx-auto max-w-6xl px-4 pt-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Affiche bilan santé pour le comptoir</h1>
          <p className="text-sm text-muted-foreground">Aperçu A4 ci-dessous. Le QR code ouvre le parcours bilan : <span className="font-mono">{url}</span></p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={savePdf} disabled={pdfing} data-testid="pdf-bilan-poster-btn" variant="outline" className="rounded-xl h-12 gap-2"><FileDown className="h-5 w-5" /> {pdfing ? "Génération…" : "Enregistrer en PDF"}</Button>
          <Button onClick={() => window.print()} data-testid="print-bilan-poster-btn" className="rounded-xl h-12 gap-2"><Printer className="h-5 w-5" /> Imprimer l'affiche</Button>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-6 flex justify-center">
        <div id="print-area" data-testid="bilan-poster-a4"
          className="bg-white text-slate-900 shadow-xl border border-slate-200 rounded-2xl overflow-hidden flex flex-col"
          style={{ width: "210mm", minHeight: "297mm" }}>
          <div className="h-3 w-full" style={{ backgroundColor: ACCENT }} />
          <div className="flex-1 flex flex-col items-center text-center px-16 py-16">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-14 w-14 rounded-2xl grid place-items-center text-white" style={{ backgroundColor: ACCENT }}><HeartPulse className="h-8 w-8" /></div>
              <div className="text-left">
                <div className="text-3xl font-heading font-bold tracking-tight">{PHARMACY.name}</div>
                <div className="font-semibold" style={{ color: ACCENT }}>Bilan de prévention santé</div>
              </div>
            </div>

            <span className="mt-10 inline-block text-sm font-bold uppercase tracking-[0.2em] text-white rounded-full px-5 py-2" style={{ backgroundColor: ACCENT }} data-testid="bilan-poster-badge">Bilan santé</span>
            <h2 className="font-heading text-5xl font-bold tracking-tight mt-6 leading-tight">Réalisez votre<br />bilan santé</h2>
            <p className="text-xl text-slate-600 mt-6 max-w-xl">Scannez pour commencer votre démarche, puis présentez-vous au comptoir avec votre carte Vitale.</p>

            <div className="mt-12 p-6 rounded-3xl border-4 bg-white" style={{ borderColor: ACCENT }} data-testid="bilan-poster-qr">
              <QRCodeSVG value={url} size={340} level="M" bgColor="#ffffff" fgColor="#3b0764" includeMargin={false} />
            </div>
            <div className="mt-4 inline-flex items-center gap-2 font-semibold text-lg" style={{ color: ACCENT }}><Smartphone className="h-5 w-5" /> Pointez l'appareil photo de votre téléphone</div>

            <div className="grid grid-cols-3 gap-6 mt-14 w-full max-w-2xl text-slate-700">
              <Step icon={Smartphone} n="1" t="Scannez le QR code" />
              <Step icon={ClipboardCheck} n="2" t="Commencez votre démarche de bilan" />
              <Step icon={Store} n="3" t="Présentez-vous au comptoir avec votre carte Vitale" />
            </div>

            <div className="mt-auto pt-14 text-slate-500">
              <p className="font-semibold text-slate-700">{PHARMACY.address}</p>
              <p>Tél. {PHARMACY.phone}</p>
              <p className="text-sm mt-2">Le bilan est réalisé avec votre pharmacien. Aucune donnée de santé n'est saisie en ligne. <span className="text-red-600 font-semibold">En cas d'urgence, appelez le 15 ou le 112.</span></p>
            </div>
          </div>
          <div className="h-3 w-full" style={{ backgroundColor: ACCENT }} />
        </div>
      </div>
    </div>
  );
}

function Step({ icon: Icon, n, t }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="h-12 w-12 rounded-full grid place-items-center relative" style={{ backgroundColor: "#7C3AED14", color: ACCENT }}>
        <Icon className="h-6 w-6" />
        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full text-white text-xs grid place-items-center font-bold" style={{ backgroundColor: ACCENT }}>{n}</span>
      </div>
      <p className="text-sm font-medium leading-tight">{t}</p>
    </div>
  );
}
